import React, { useState, useEffect } from 'react';
import { Form, Button, Table, Card, Row, Col, ListGroup, Modal, InputGroup, Collapse } from 'react-bootstrap';
import { Pencil, Trash, Plus, FileEarmarkPlus, ListTask } from 'react-bootstrap-icons';
import ComplexRuleBuilder from './ComplexRuleBuilder';
import ConfirmationModal from '../../shared/components/ConfirmationModal';
import * as IconComponents from 'react-bootstrap-icons'; // Import all icons for dynamic rendering
import './RulesTab.css'; // Import new CSS file

const RulesTab = ({ settings, onSave, categories, paymentSources }) => {
  const [rules, setRules] = useState([]);
  const [newRule, setNewRule] = useState({
    category: '',
    subcategory: '',
    logical_operator: 'AND',
    conditions: [{ field: 'Description', rule_type: 'contains', value: '' }],
    note: ''
  });
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [currentRuleEdit, setCurrentRuleEdit] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalTitle, setConfirmModalTitle] = useState('');
  const [confirmModalMessage, setConfirmModalMessage] = useState('');
  const [confirmModalAction, setConfirmModalAction] = useState(null);

  useEffect(() => {
    setRules(settings.rules || []);
  }, [settings]);

  const handleAddRule = () => {
    if (newRule.category && newRule.conditions.length > 0 && newRule.conditions.some(c => c.value)) {
      const updatedRules = [...rules, newRule];
      setRules(updatedRules);
      onSave({ ...settings, rules: updatedRules });
      setNewRule({
        category: '',
        subcategory: '',
        logical_operator: 'AND',
        conditions: [{ field: 'Description', rule_type: 'contains', value: '' }],
        note: ''
      });
    } else {
      alert('Please fill in all required fields for the rule.');
    }
  };

  const handleDeleteRuleClick = (index) => {
    setConfirmModalTitle('Confirm Rule Deletion');
    setConfirmModalMessage('Are you sure you want to delete this rule? This action cannot be undone.');
    setConfirmModalAction(() => () => _deleteRule(index));
    setShowConfirmModal(true);
  };

  const _deleteRule = (index) => {
    const updatedRules = rules.filter((_, i) => i !== index);
    setRules(updatedRules);
    onSave({ ...settings, rules: updatedRules });
    setShowConfirmModal(false);
  };

  const handleUpdateRule = (index, updatedRule) => {
    const updatedRules = [...rules];
    updatedRules[index] = updatedRule;
    setRules(updatedRules);
    onSave({ ...settings, rules: updatedRules });
  };

  const handleEditRuleClick = (index) => {
    setCurrentRuleEdit({ index, ...rules[index] });
    setShowRuleModal(true);
  };

  const handleSaveRuleModal = () => {
    const { index, ...ruleToSave } = currentRuleEdit;
    handleUpdateRule(index, ruleToSave);
    setShowRuleModal(false);
  };

  const getIconComponent = (iconName, size = 16, className = "") => {
    if (!iconName) return null;
    const IconComponent = IconComponents[iconName];
    return IconComponent ? <IconComponent size={size} className={className} /> : null;
  };

  return (
    <Card className="rules-card">
      <Card.Header>
        <FileEarmarkPlus className="me-2" />
        Add New Rule
      </Card.Header>
      <Card.Body>
        <div className="add-rule-container">
          <ComplexRuleBuilder
            rule={newRule}
            onRuleChange={setNewRule}
            categories={categories}
            paymentSources={paymentSources}
          />
          <Button variant="primary" onClick={handleAddRule} className="mt-3">
            <Plus className="me-2" />Add Rule
          </Button>
        </div>
      </Card.Body>

      <Card.Header>
        <ListTask className="me-2" />
        Existing Rules
      </Card.Header>
      <Card.Body>
        <Table responsive striped bordered hover className="expense-table">
          <thead>
            <tr>
              <th>Category</th>
              <th>Subcategory</th>
              <th style={{width: '40%'}}>Conditions</th>
              <th>Note</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rules && rules.map((rule, index) => {
              const category = categories.find(cat => cat.name === rule.category);
              return (
              <tr key={index}>
                <td>
                  <div className="d-flex align-items-center">
                    {getIconComponent(category?.icon, 18, "me-1")}
                    {rule.category}
                  </div>
                </td>
                <td>{rule.subcategory}</td>
                <td className="conditions-cell">
                  {rule.conditions && rule.conditions.map((c, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <div className="logical-operator">{rule.logical_operator}</div>}
                      <div>
                        <span className="condition-field">{c.field}</span>{' '}
                        <span className="condition-rule-type">{c.rule_type}</span>{' '}
                        <span className="condition-value">{JSON.stringify(c.value)}</span>
                      </div>
                    </React.Fragment>
                  ))}
                </td>
                <td>{rule.note}</td>
                <td>
                  <Button variant="outline-primary" size="sm" onClick={() => handleEditRuleClick(index)} className="me-2"><Pencil /></Button>
                  <Button variant="outline-danger" size="sm" onClick={() => handleDeleteRuleClick(index)}><Trash /></Button>
                </td>
              </tr>
            );
          })}
          </tbody>
        </Table>
      </Card.Body>

      <Modal show={showRuleModal} onHide={() => setShowRuleModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Rule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentRuleEdit && (
            <ComplexRuleBuilder
              rule={currentRuleEdit}
              onRuleChange={setCurrentRuleEdit}
              categories={categories}
              paymentSources={paymentSources}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRuleModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSaveRuleModal}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmationModal
        show={showConfirmModal}
        title={confirmModalTitle}
        message={confirmModalMessage}
        onConfirm={confirmModalAction}
        onCancel={() => setShowConfirmModal(false)}
      />
    </Card>
  );
};

export default RulesTab;
